// LoginTracker/LoginTrackingDashboard.jsx
// Tabs: Activity | Users | Devices & IPs | Device control | Access control | Security settings | Security events
import React, { useState, useEffect, useMemo } from 'react';
import firebaseDB from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { PATHS } from './dbPaths';
import { useAccessLists } from './useAccessLists';
import { useConfirm } from './ConfirmDialog';
import IPWhitelistManager from './IPWhitelistManager';
import IPUsageAnalytics from './IPUsageAnalytics';
import DeviceControlPanel from './DeviceControlPanel';
import SecuritySettingsPanel from './SecuritySettingsPanel';
import { UserCell, deviceIcon } from './UserAvatar';
import { normalizeUser, USER_TYPES, USER_TYPE_LABELS } from './userInfo';
import FilterBar, { useDateFilter, inRange, dateLabel, Sel, CheckBox } from './FilterBar';
import { sanitizeKey } from './ipUtils';
import { isLoginRecord, normalizeLog, isKnownIP, formatDate, csvCell } from './logUtils';
import { forceLogoutUser, migrateLegacyLoginData } from './trackUserLogin';

const ONLINE_MS = 5 * 60 * 1000;
const SEVERITY = { HIGH: 'bg-danger', MEDIUM: 'bg-warning text-dark', LOW: 'bg-secondary' };

/* ---------------- pagination (module level so it does not remount on every render) ---------------- */
const Pagination = ({ page, totalPages, onPage, from, to, total }) => {
    if (totalPages <= 1) return null;
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);
    start = Math.max(1, end - max + 1);
    const nums = [];
    for (let i = start; i <= end; i += 1) nums.push(i);
    const btn = 'page-link bg-dark text-white border-secondary';
    return (
        <>
            <small className="text-warning text-center d-block m-2">Showing {from} to {to} of {total} entries</small>
            <div className="card-footer bg-secondary bg-opacity-10">
                <ul className="pagination pagination-sm mb-0 justify-content-center">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}><button className={btn} onClick={() => onPage(1)}><i className="bi bi-chevron-double-left" /></button></li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}><button className={btn} onClick={() => onPage(page - 1)}><i className="bi bi-chevron-left" /></button></li>
                    {nums.map((n) => (
                        <li key={n} className={`page-item ${n === page ? 'active' : ''}`}><button className={btn} onClick={() => onPage(n)}>{n}</button></li>
                    ))}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}><button className={btn} onClick={() => onPage(page + 1)}><i className="bi bi-chevron-right" /></button></li>
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}><button className={btn} onClick={() => onPage(totalPages)}><i className="bi bi-chevron-double-right" /></button></li>
                </ul>
            </div>
        </>
    );
};

/* ---------------- security events ---------------- */
const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((x, y) => String(x).localeCompare(String(y)));

const SecurityEventsPanel = () => {
    const [events, setEvents] = useState([]);
    const [limit, setLimit] = useState(200);
    const [search, setSearch] = useState('');
    const [severity, setSeverity] = useState('all');
    const [type, setType] = useState('all');
    const date = useDateFilter();

    useEffect(() => {
        const ref = firebaseDB.child(PATHS.events).orderByKey().limitToLast(limit);
        const cb = ref.on('value', (snap) => {
            const v = snap.val() || {};
            setEvents(Object.entries(v).map(([id, e]) => ({ id, ...e, when: e.ts || new Date(e.timestamp || 0).getTime() }))
                .sort((a, b) => b.when - a.when));
        }, (e) => console.error('SecurityEvents read failed', e));
        return () => ref.off('value', cb);
    }, [limit]);

    const typeOptions = useMemo(() => uniq(events.map((e) => e.type)), [events]);
    const shown = useMemo(() => {
        const q = search.trim().toLowerCase();
        return events.filter((e) => {
            if (!inRange(e.when, date.range)) return false;
            if (severity !== 'all' && (e.severity || 'LOW') !== severity) return false;
            if (type !== 'all' && e.type !== type) return false;
            if (!q) return true;
            return [e.type, e.name, e.email, e.identifier, e.userId, e.ipAddress, e.reason, e.deviceName, e.by]
                .some((f) => f && String(f).toLowerCase().includes(q));
        });
    }, [events, date.range, severity, type, search]);

    const detail = (e) => [e.name || e.email || e.identifier || e.userId, e.deviceName, e.reason, e.by && `by ${e.by}`].filter(Boolean).join(' · ');

    const chips = [];
    if (search.trim()) chips.push({ label: `Search: ${search.trim()}`, onRemove: () => setSearch('') });
    if (date.active) chips.push({ label: dateLabel(date), onRemove: date.clear });
    if (severity !== 'all') chips.push({ label: `Level: ${severity}`, onRemove: () => setSeverity('all') });
    if (type !== 'all') chips.push({ label: `Event: ${type}`, onRemove: () => setType('all') });
    const clearAll = () => { setSearch(''); date.clear(); setSeverity('all'); setType('all'); };

    return (
        <>
            <FilterBar
                date={date} search={search} onSearch={setSearch} placeholder="Event, user, IP, device, reason..."
                chips={chips} onClearAll={clearAll} summary={`Showing ${shown.length} of ${events.length} events`}
                extraCount={(severity !== 'all' ? 1 : 0) + (type !== 'all' ? 1 : 0)}
                extra={(
                    <>
                        <Sel label="Level" value={severity} onChange={setSeverity} options={[{ value: 'HIGH', label: 'High' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'LOW', label: 'Low' }]} />
                        <Sel label="Event type" value={type} onChange={setType} options={typeOptions} />
                        <Sel label="Load" value={String(limit)} onChange={(v) => setLimit(Number(v))} allLabel={null}
                            options={[{ value: '200', label: 'Last 200' }, { value: '500', label: 'Last 500' }, { value: '1000', label: 'Last 1000' }]} />
                    </>
                )}
            />
            <div className="card bg-dark border-secondary">
                <div className="card-header bg-info bg-opacity-25 border-info">
                    <h6 className="mb-0 text-white"><i className="bi bi-exclamation-triangle me-2" />Security events ({shown.length})</h6>
                </div>
                <div className="table-responsive">
                    <table className="table table-dark table-hover align-middle mb-0">
                        <thead><tr><th>Time</th><th>Event</th><th>Level</th><th>IP</th><th>Details</th></tr></thead>
                        <tbody>
                            {shown.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">No events match these filters</td></tr>
                            ) : shown.map((e) => (
                                <tr key={e.id}>
                                    <td><small>{formatDate(e.when)}</small></td>
                                    <td><small className="font-monospace">{e.type}</small></td>
                                    <td><span className={`badge ${SEVERITY[e.severity] || 'bg-secondary'}`}>{e.severity || 'LOW'}</span></td>
                                    <td><code className="text-warning">{e.ipAddress || '—'}</code></td>
                                    <td><small className="text-muted">{detail(e) || '—'}</small></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

/* ---------------- filter helpers ---------------- */
const ADV_DEFAULT = { status: 'all', risk: 'all', device: 'all', os: 'all', browser: 'all', country: 'all', networkChanged: false, newDevice: false };
const USER_FILTER_DEFAULT = { status: 'all', suspicious: false };
const SORTERS = {
    newest: (a, b) => b.t - a.t,
    oldest: (a, b) => a.t - b.t,
    user: (a, b) => String(a.name).localeCompare(String(b.name)) || b.t - a.t,
    logins: (a, b) => b.count - a.count || b.t - a.t,
};
const CARD_LABELS = { uniqueUsers: 'One row per user', uniqueDevices: 'One row per device', uniqueIPs: 'One row per IP', todayLogins: "Today's logins", suspicious: 'Suspicious only' };

/* ---------------- main ---------------- */
const LoginTrackingDashboard = () => {
    const auth = useAuth() || {};
    const adminName = normalizeUser(auth.currentUser || auth.user || {}).name;
    const lists = useAccessLists(); // ONE set of live listeners shared by every tab
    const [confirm, dialog] = useConfirm();

    const [tab, setTab] = useState('activity');
    const [rawLogs, setRawLogs] = useState([]); // [[id, record], ...] exactly as stored
    const [devices, setDevices] = useState({});
    const [profiles, setProfiles] = useState({});
    const [legacyCount, setLegacyCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [limit, setLimit] = useState(1000);

    // filters shared by Activity, Users and Devices & IPs
    const date = useDateFilter();
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    // extra filters for Activity and Devices & IPs
    const [adv, setAdv] = useState(ADV_DEFAULT);
    // extra filters for Users
    const [userF, setUserF] = useState(USER_FILTER_DEFAULT);
    const [userSort, setUserSort] = useState('recent');
    // Activity only
    const [cardFilter, setCardFilter] = useState(null);
    const [sortBy, setSortBy] = useState('newest');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [tick, setTick] = useState(0); // re-render every minute so "online" dots stay correct

    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 60000);
        return () => clearInterval(id);
    }, []);

    // ONE live listener for logins
    useEffect(() => {
        setLoading(true);
        const ref = firebaseDB.child(PATHS.loginData).orderByKey().limitToLast(limit);
        const cb = ref.on('value', (snap) => {
            const entries = Object.entries(snap.val() || {});
            const logs = entries.filter(([, d]) => isLoginRecord(d));
            setLegacyCount(entries.length - logs.length);
            setRawLogs(logs);
            setLastUpdated(new Date());
            setError('');
            setLoading(false);
        }, (err) => {
            console.error('LoginData read failed', err);
            setError('Could not read login data. Check your Firebase rules.');
            setLoading(false);
        });
        return () => ref.off('value', cb);
    }, [limit]);

    // Users and devices are stored once and joined onto every login row
    useEffect(() => {
        const defs = [[PATHS.userProfiles, setProfiles], [PATHS.devices, setDevices]];
        const subs = defs.map(([path, set]) => {
            const ref = firebaseDB.child(path);
            return { ref, cb: ref.on('value', (snap) => set(snap.val() || {}), (e) => console.warn(`${path} read failed`, e)) };
        });
        return () => subs.forEach(({ ref, cb }) => ref.off('value', cb));
    }, []);

    const loginLogs = useMemo(
        () => rawLogs.map(([id, d]) => normalizeLog(id, d, { devices, profiles })).sort((a, b) => b.t - a.t),
        [rawLogs, devices, profiles],
    );

    /* ---------- derived data ---------- */
    const stats = useMemo(() => {
        const startToday = new Date(); startToday.setHours(0, 0, 0, 0);
        const st = startToday.getTime();
        return {
            totalLogins: loginLogs.reduce((n, l) => n + l.count, 0),
            uniqueUsers: new Set(loginLogs.map((l) => l.userId)).size,
            uniqueDevices: new Set(loginLogs.map((l) => l.deviceKey)).size,
            uniqueIPs: new Set(loginLogs.filter((l) => isKnownIP(l.ipAddress)).map((l) => l.ipAddress)).size,
            todayLogins: loginLogs.reduce((n, l) => (l.t >= st ? n + (l.firstT >= st ? l.count : 1) : n), 0),
            suspicious: loginLogs.filter((l) => l.status === 'SUSPICIOUS').length,
        };
    }, [loginLogs]);

    const knownIPs = useMemo(() => {
        const m = new Map();
        loginLogs.forEach((l) => {
            if (!isKnownIP(l.ipAddress)) return;
            const e = m.get(l.ipAddress) || { ip: l.ipAddress, count: 0, last: 0 };
            e.count += l.count; e.last = Math.max(e.last, l.t);
            m.set(l.ipAddress, e);
        });
        return [...m.values()].sort((a, b) => b.last - a.last);
    }, [loginLogs]);

    // 1) shared filters: date, user type, search
    const options = useMemo(() => ({
        os: uniq(loginLogs.map((l) => l.os)),
        browser: uniq(loginLogs.map((l) => l.browser)),
        country: uniq(loginLogs.map((l) => l.country).filter((c) => c && c !== 'Unknown')),
    }), [loginLogs]);

    const base = useMemo(() => {
        const q = search.trim().toLowerCase();
        return loginLogs.filter((l) => {
            if (!inRange(l.t, date.range)) return false;
            if (typeFilter !== 'all' && l.userType !== typeFilter) return false;
            if (!q) return true;
            return [l.userId, l.email, l.name, l.ipAddress, l.deviceName, l.os, l.browser, l.city, l.country, l.isp, USER_TYPE_LABELS[l.userType], l.status]
                .some((f) => f && String(f).toLowerCase().includes(q));
        });
    }, [loginLogs, date.range, typeFilter, search]);

    // 2) extra filters (Activity, Devices & IPs)
    const advFiltered = useMemo(() => base.filter((l) => {
        if (adv.status === 'suspicious' && l.status !== 'SUSPICIOUS') return false;
        if (adv.status === 'success' && l.status === 'SUSPICIOUS') return false;
        if (adv.risk !== 'all' && (l.riskLevel || 'LOW') !== adv.risk) return false;
        if (adv.device !== 'all' && l.deviceKind !== adv.device) return false;
        if (adv.os !== 'all' && l.os !== adv.os) return false;
        if (adv.browser !== 'all' && l.browser !== adv.browser) return false;
        if (adv.country !== 'all' && l.country !== adv.country) return false;
        if (adv.networkChanged && !l.ipChanged) return false;
        if (adv.newDevice && !l.newDevice) return false;
        return true;
    }), [base, adv]);

    // 3) Activity: stat-card filter, then sort
    const activityList = useMemo(() => {
        let list = advFiltered; // newest first here, so "one row per ..." keeps the latest row
        if (cardFilter === 'uniqueUsers') { const s = new Set(); list = list.filter((l) => (s.has(l.userId) ? false : s.add(l.userId))); }
        else if (cardFilter === 'uniqueDevices') { const s = new Set(); list = list.filter((l) => (s.has(l.deviceKey) ? false : s.add(l.deviceKey))); }
        else if (cardFilter === 'uniqueIPs') { const s = new Set(); list = list.filter((l) => (s.has(l.ipAddress) ? false : s.add(l.ipAddress))); }
        else if (cardFilter === 'todayLogins') { const d = new Date(); d.setHours(0, 0, 0, 0); list = list.filter((l) => l.t >= d.getTime()); }
        else if (cardFilter === 'suspicious') list = list.filter((l) => l.status === 'SUSPICIOUS');
        return sortBy === 'newest' ? list : [...list].sort(SORTERS[sortBy]);
    }, [advFiltered, cardFilter, sortBy]);

    // Users tab: built from the shared filters, then its own filters
    const userRows = useMemo(() => {
        const m = new Map();
        base.forEach((l) => {
            let u = m.get(l.userId);
            if (!u) {
                u = { userId: l.userId, name: l.name, email: l.email, photo: l.photo, type: l.userType, logins: 0, devices: new Set(), ips: new Set(), lastLogin: l.t, lastIP: l.ipAddress, lastDevice: l.deviceName, suspicious: 0 };
                m.set(l.userId, u);
            }
            u.logins += l.count;
            u.devices.add(l.deviceKey);
            if (isKnownIP(l.ipAddress)) u.ips.add(l.ipAddress);
            if (l.status === 'SUSPICIOUS') u.suspicious += 1;
        });
        const nowT = Date.now();
        let rows = [...m.values()].map((u) => {
            const lastActive = (profiles[sanitizeKey(u.userId)] || {}).lastActive || 0;
            return { ...u, lastActive, online: !!lastActive && nowT - lastActive < ONLINE_MS };
        });
        if (userF.status === 'online') rows = rows.filter((u) => u.online);
        if (userF.status === 'offline') rows = rows.filter((u) => !u.online);
        if (userF.suspicious) rows = rows.filter((u) => u.suspicious > 0);
        const sorters = {
            recent: (a, b) => b.lastLogin - a.lastLogin,
            logins: (a, b) => b.logins - a.logins,
            name: (a, b) => String(a.name).localeCompare(String(b.name)),
        };
        return rows.sort(sorters[userSort]);
    }, [base, profiles, userF, userSort, tick]);

    const filterSig = JSON.stringify([search, date.range, typeFilter, adv, cardFilter, sortBy, itemsPerPage]);
    useEffect(() => { setCurrentPage(1); }, [filterSig]);

    const totalPages = Math.max(1, Math.ceil(activityList.length / itemsPerPage));
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedLogs = useMemo(() => activityList.slice(startIndex, endIndex), [activityList, startIndex, endIndex]);
    const pendingDevices = Object.values(lists.deviceRules).filter((r) => r.status === 'pending').length;

    /* ---------- actions ---------- */
    const clearAllFilters = () => {
        setSearch(''); date.clear(); setTypeFilter('all'); setAdv(ADV_DEFAULT); setUserF(USER_FILTER_DEFAULT); setCardFilter(null);
    };

    // chips = what is active on the CURRENT tab
    const isAdvTab = tab === 'activity' || tab === 'devices';
    const chips = [];
    if (search.trim()) chips.push({ label: `Search: ${search.trim()}`, onRemove: () => setSearch('') });
    if (date.active) chips.push({ label: `Date: ${dateLabel(date)}`, onRemove: date.clear });
    if (typeFilter !== 'all') chips.push({ label: `Type: ${USER_TYPE_LABELS[typeFilter]}`, onRemove: () => setTypeFilter('all') });
    if (isAdvTab) {
        if (adv.status !== 'all') chips.push({ label: `Status: ${adv.status === 'suspicious' ? 'Suspicious' : 'Success'}`, onRemove: () => setAdv((p) => ({ ...p, status: 'all' })) });
        if (adv.risk !== 'all') chips.push({ label: `Risk: ${adv.risk}`, onRemove: () => setAdv((p) => ({ ...p, risk: 'all' })) });
        if (adv.device !== 'all') chips.push({ label: `Device: ${adv.device}`, onRemove: () => setAdv((p) => ({ ...p, device: 'all' })) });
        if (adv.os !== 'all') chips.push({ label: `OS: ${adv.os}`, onRemove: () => setAdv((p) => ({ ...p, os: 'all' })) });
        if (adv.browser !== 'all') chips.push({ label: `Browser: ${adv.browser}`, onRemove: () => setAdv((p) => ({ ...p, browser: 'all' })) });
        if (adv.country !== 'all') chips.push({ label: `Country: ${adv.country}`, onRemove: () => setAdv((p) => ({ ...p, country: 'all' })) });
        if (adv.networkChanged) chips.push({ label: 'Network changed', onRemove: () => setAdv((p) => ({ ...p, networkChanged: false })) });
        if (adv.newDevice) chips.push({ label: 'New device', onRemove: () => setAdv((p) => ({ ...p, newDevice: false })) });
    }
    if (tab === 'activity' && cardFilter) chips.push({ label: CARD_LABELS[cardFilter] || 'Card filter', onRemove: () => setCardFilter(null) });
    if (tab === 'users') {
        if (userF.status !== 'all') chips.push({ label: `Status: ${userF.status === 'online' ? 'Online' : 'Offline'}`, onRemove: () => setUserF((p) => ({ ...p, status: 'all' })) });
        if (userF.suspicious) chips.push({ label: 'Has suspicious logins', onRemove: () => setUserF((p) => ({ ...p, suspicious: false })) });
    }
    const extraCount = chips.filter((c) => !c.label.startsWith('Search:') && !c.label.startsWith('Date:')).length;

    const exportToCSV = () => {
        const headers = ['User ID', 'Name', 'Email', 'Type', 'IP Address', 'Previous IP', 'First login', 'Last login', 'Logins', 'Device', 'OS', 'Browser', 'Country', 'City', 'ISP', 'Status', 'Risk', 'Reason'];
        const rows = activityList.map((l) => [l.userId, l.name, l.email, l.userType, l.ipAddress, l.previousIP || '', formatDate(l.firstT), formatDate(l.t), l.count, l.deviceName,
            `${l.os || ''} ${l.osVersion || ''}`.trim(), l.browser, l.country, l.city, l.isp, l.status || 'SUCCESS', l.riskLevel || '', l.suspiciousReason || '']);
        const csv = [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `login-logs-${new Date().toISOString().split('T')[0]}.csv`; a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleForceLogout = async (u) => {
        const ok = await confirm({
            title: 'Sign this user out?', tone: 'warning', confirmText: 'Force logout',
            message: `${u.name} will be signed out everywhere within a few seconds and must log in again.`,
        });
        if (!ok) return;
        const r = await forceLogoutUser(u.userId, adminName);
        setNotice(r.success ? `${u.name} will be signed out within a few seconds.` : r.error);
    };

    const handleCleanup = async () => {
        const ok = await confirm({
            title: 'Clean up old data?', tone: 'warning', confirmText: 'Clean up',
            message: `Found ${legacyCount} old records inside LoginData that are not logins (events, per-user copies, IP stats).\n\nEvents are moved to Security events and the rest are deleted.`,
        });
        if (!ok) return;
        try {
            const r = await migrateLegacyLoginData();
            setNotice(`Cleanup done: ${r.moved} events moved, ${r.removed} old nodes removed.`);
        } catch (e) { setNotice(`Cleanup failed: ${e.message}`); }
    };

    const cards = [
        { key: 'totalLogins', label: 'Total logins', value: stats.totalLogins, tone: 'success', icon: 'bi-box-arrow-in-right' },
        { key: 'uniqueUsers', label: 'Users', value: stats.uniqueUsers, tone: 'info', icon: 'bi-people' },
        { key: 'uniqueDevices', label: 'Devices', value: stats.uniqueDevices, tone: 'primary', icon: 'bi-phone' },
        { key: 'uniqueIPs', label: 'IP addresses', value: stats.uniqueIPs, tone: 'warning', icon: 'bi-diagram-3' },
        { key: 'todayLogins', label: "Today's logins", value: stats.todayLogins, tone: 'primary', icon: 'bi-calendar-date' },
        { key: 'suspicious', label: 'Suspicious', value: stats.suspicious, tone: 'danger', icon: 'bi-exclamation-triangle' },
    ];
    const tabs = [
        ['activity', 'Activity', 'bi-list', 0],
        ['users', `Users (${userRows.length})`, 'bi-people', 0],
        ['devices', 'Devices & IPs', 'bi-devices', 0],
        ['control', 'Device control', 'bi-phone-flip', pendingDevices],
        ['access', 'Access control', 'bi-shield-check', 0],
        ['settings', 'Security settings', 'bi-sliders', 0],
        ['security', 'Security events', 'bi-exclamation-triangle', 0],
    ];

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="card bg-dark border-secondary text-center">
                    <div className="card-body py-5">
                        <div className="spinner-border text-info mb-3" role="status"><span className="visually-hidden">Loading...</span></div>
                        <h5 className="text-white">Loading Login Tracking Dashboard...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4 bg-secondary bg-opacity-10 rounded-4">
            {/* header */}
            <div className="card bg-dark border-primary mb-4">
                <div className="card-header bg-primary bg-opacity-25 border-primary d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h4 className="card-title mb-0 text-info"><i className="bi bi-shield-check me-2" />Login Tracking Dashboard</h4>
                        <small className="text-warning">Users, devices, IP addresses and access rules</small>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {legacyCount > 0 && <button className="btn btn-sm btn-outline-warning" onClick={handleCleanup}><i className="bi bi-broom me-1" />Clean old data ({legacyCount})</button>}
                        <select className="form-select form-select-sm bg-dark text-white border-secondary" style={{ width: 'auto' }} value={limit} onChange={(e) => setLimit(Number(e.target.value))} title="How many recent login records to load">
                            <option value="500">Last 500</option><option value="1000">Last 1000</option><option value="3000">Last 3000</option><option value="10000">Last 10000</option>
                        </select>
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {notice && (
                <div className="alert alert-info d-flex justify-content-between py-2">
                    <span>{notice}</span><button className="btn-close" onClick={() => setNotice('')} />
                </div>
            )}

            {/* stat cards */}
            <div className="row mb-2">
                {cards.map((c) => (
                    <div key={c.key} className="col-xl-2 col-md-4 col-6 mb-3">
                        <div
                            className={`card h-100 border-${c.tone} ${cardFilter === c.key ? 'border-3' : ''}`}
                            style={{ cursor: 'pointer', backgroundColor: cardFilter === c.key ? 'rgba(255,255,255,0.08)' : '' }}
                            onClick={() => { setCardFilter(cardFilter === c.key ? null : c.key); setTab('activity'); }}
                        >
                            <div className="card-body py-3">
                                <div className="d-flex justify-content-between">
                                    <div><h6 className="card-title text-muted small">{c.label}</h6><h3 className={`text-${c.tone} mb-0`}>{c.value}</h3></div>
                                    <i className={`bi ${c.icon} fs-3 text-${c.tone} opacity-50 align-self-center`} />
                                </div>
                                {cardFilter === c.key && <small className={`text-${c.tone}`}><i className="bi bi-funnel me-1" />Filtering</small>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* tabs */}
            <ul className="nav nav-pills mb-3 gap-1 flex-wrap">
                {tabs.map(([key, label, icon, badge]) => (
                    <li key={key} className="nav-item">
                        <button className={`nav-link ${tab === key ? 'active' : 'text-info'}`} onClick={() => setTab(key)}>
                            <i className={`bi ${icon} me-1`} />{label}
                            {badge > 0 && <span className="badge bg-danger ms-2">{badge}</span>}
                        </button>
                    </li>
                ))}
            </ul>

            {/* ===== filters (Activity, Users, Devices & IPs) ===== */}
            {(tab === 'activity' || tab === 'users' || tab === 'devices') && (
                <FilterBar
                    date={date}
                    search={search}
                    onSearch={setSearch}
                    placeholder={tab === 'users' ? 'Name, email, IP, device...' : 'Name, email, IP, device, city...'}
                    chips={chips}
                    extraCount={extraCount}
                    onClearAll={clearAllFilters}
                    summary={tab === 'activity'
                        ? `Showing ${activityList.length} of ${loginLogs.length} login records`
                        : tab === 'users' ? `Showing ${userRows.length} users` : `Using ${advFiltered.length} of ${loginLogs.length} login records`}
                    actions={tab === 'activity' ? (
                        <button type="button" className="btn btn-sm btn-outline-success" onClick={exportToCSV} disabled={activityList.length === 0}>
                            <i className="bi bi-download me-1" />Export ({activityList.length})
                        </button>
                    ) : null}
                    extra={(
                        <>
                            <Sel label="User type" value={typeFilter} onChange={setTypeFilter} options={USER_TYPES} />
                            {isAdvTab && (
                                <>
                                    <Sel label="Status" value={adv.status} onChange={(v) => setAdv((p) => ({ ...p, status: v }))} options={[{ value: 'success', label: 'Success' }, { value: 'suspicious', label: 'Suspicious' }]} />
                                    <Sel label="Risk level" value={adv.risk} onChange={(v) => setAdv((p) => ({ ...p, risk: v }))} options={[{ value: 'LOW', label: 'Low' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }]} />
                                    <Sel label="Device type" value={adv.device} onChange={(v) => setAdv((p) => ({ ...p, device: v }))} options={['Mobile', 'Tablet', 'Desktop']} />
                                    <Sel label="Operating system" value={adv.os} onChange={(v) => setAdv((p) => ({ ...p, os: v }))} options={options.os} />
                                    <Sel label="Browser" value={adv.browser} onChange={(v) => setAdv((p) => ({ ...p, browser: v }))} options={options.browser} />
                                    <Sel label="Country" value={adv.country} onChange={(v) => setAdv((p) => ({ ...p, country: v }))} options={options.country} />
                                    <CheckBox label="Network changed" checked={adv.networkChanged} onChange={(v) => setAdv((p) => ({ ...p, networkChanged: v }))} />
                                    <CheckBox label="New device" checked={adv.newDevice} onChange={(v) => setAdv((p) => ({ ...p, newDevice: v }))} />
                                </>
                            )}
                            {tab === 'users' && (
                                <>
                                    <Sel label="Status" value={userF.status} onChange={(v) => setUserF((p) => ({ ...p, status: v }))} options={[{ value: 'online', label: 'Online now' }, { value: 'offline', label: 'Offline' }]} />
                                    <Sel label="Sort by" value={userSort} onChange={setUserSort} allLabel={null} options={[{ value: 'recent', label: 'Last login' }, { value: 'logins', label: 'Most logins' }, { value: 'name', label: 'Name A-Z' }]} />
                                    <CheckBox label="Has suspicious logins" checked={userF.suspicious} onChange={(v) => setUserF((p) => ({ ...p, suspicious: v }))} />
                                </>
                            )}
                        </>
                    )}
                />
            )}

            {/* ===== activity ===== */}
            {tab === 'activity' && (
                <>
                    <div className="card bg-dark border-secondary">
                        <div className="card-header bg-info bg-opacity-25 border-info d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h6 className="mb-0 text-white">
                                <i className="bi bi-list me-2" />Login activity ({activityList.length})
                                {chips.length > 0 && <span className="badge bg-warning text-dark ms-2">Filtered</span>}
                            </h6>
                            <div className="d-flex align-items-center gap-3">
                                {lastUpdated && <span className="badge bg-warning text-dark">Updated {lastUpdated.toLocaleTimeString('en-IN')}</span>}
                                <select className="form-select form-select-sm bg-dark text-white border-secondary" style={{ width: 'auto' }} value={sortBy} onChange={(e) => setSortBy(e.target.value)} title="Sort">
                                    <option value="newest">Newest first</option><option value="oldest">Oldest first</option><option value="user">User A-Z</option><option value="logins">Most logins</option>
                                </select>
                                <select className="form-select form-select-sm bg-dark text-white border-secondary" style={{ width: 'auto' }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                                    <option value="10">10 per page</option><option value="20">20 per page</option><option value="50">50 per page</option><option value="100">100 per page</option>
                                </select>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-dark table-hover align-middle mb-0">
                                    <thead><tr><th>User</th><th>Device</th><th>IP address</th><th>Last login</th><th>Location</th><th>ISP</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {displayedLogs.length === 0 ? (
                                            <tr><td colSpan="7" className="text-center py-4"><i className="bi bi-inbox fs-3 text-muted d-block mb-2" /><span className="text-muted">No login records match these filters</span></td></tr>
                                        ) : displayedLogs.map((l) => (
                                            <tr key={l.id}>
                                                <td><UserCell name={l.name} email={l.email} photo={l.photo} type={l.userType} /></td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className={`bi ${deviceIcon(l.deviceKind)} fs-5 text-info`} />
                                                        <div>
                                                            <div>{l.deviceName}{l.deviceModelApprox && <small className="text-muted ms-1">(best guess)</small>}</div>
                                                            <small className="text-muted">{[l.os, l.osVersion].filter(Boolean).join(' ')} · {l.browser}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <code className="text-warning">{l.ipAddress}</code>
                                                    {l.ipChanged && <span className="badge bg-secondary ms-1" title={`Same device, previous IP: ${l.previousIP}`}>network changed</span>}
                                                </td>
                                                <td>
                                                    <small className="text-white">{formatDate(l.t)}</small>
                                                    {l.count > 1 && <span className="badge bg-primary ms-2" title={`${l.count} logins from this device and IP. First: ${formatDate(l.firstT)}`}>×{l.count}</span>}
                                                </td>
                                                <td><small className="text-muted">{[l.city, l.country].filter((x) => x && x !== 'Unknown').join(', ') || 'Unknown'}</small></td>
                                                <td><small className="text-muted">{l.isp || 'Unknown'}</small></td>
                                                <td>
                                                    {l.status === 'SUSPICIOUS'
                                                        ? <span className="badge bg-warning text-dark" title={l.suspiciousReason || ''}><i className="bi bi-exclamation-triangle me-1" />Suspicious</span>
                                                        : <span className="badge bg-success"><i className="bi bi-check me-1" />Success</span>}
                                                    {l.riskLevel && l.riskLevel !== 'LOW' && <small className="d-block text-muted">{l.riskLevel} risk</small>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination page={currentPage} totalPages={totalPages} onPage={(p) => setCurrentPage(Math.min(totalPages, Math.max(1, p)))}
                                from={activityList.length ? startIndex + 1 : 0} to={Math.min(endIndex, activityList.length)} total={activityList.length} />
                        </div>
                    </div>
                </>
            )}

            {/* ===== users ===== */}
            {tab === 'users' && (
                <div className="card bg-dark border-secondary">
                    <div className="card-header bg-info bg-opacity-25 border-info"><h6 className="mb-0 text-white"><i className="bi bi-people me-2" />User accounts ({userRows.length})</h6></div>
                    <div className="table-responsive">
                        <table className="table table-dark table-hover align-middle mb-0" data-tick={tick}>
                            <thead><tr><th>User</th><th>Logins</th><th>Devices</th><th>IPs</th><th>Last device</th><th>Last login</th><th>Status</th><th /></tr></thead>
                            <tbody>
                                {userRows.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center py-4 text-muted">No users match these filters</td></tr>
                                ) : userRows.map((u) => {
                                    const online = u.online;
                                    return (
                                        <tr key={u.userId}>
                                            <td><UserCell name={u.name} photo={u.photo} type={u.type} size={40} /></td>
                                            <td><span className="badge bg-primary">{u.logins}</span>{u.suspicious > 0 && <span className="badge bg-warning text-dark ms-1" title="Suspicious logins">{u.suspicious}</span>}</td>
                                            <td><span className="badge bg-secondary">{u.devices.size}</span></td>
                                            <td><span className="badge bg-secondary">{u.ips.size}</span></td>
                                            <td><small>{u.lastDevice}</small><small className="d-block text-muted"><code>{isKnownIP(u.lastIP) ? u.lastIP : ''}</code></small></td>
                                            <td><small className="text-white">{formatDate(u.lastLogin)}</small></td>
                                            <td>{online ? <span className="badge bg-success"><i className="bi bi-circle-fill me-1" style={{ fontSize: 8 }} />Online</span> : <span className="badge bg-secondary">Offline</span>}</td>
                                            <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => handleForceLogout(u)} title="Sign this user out everywhere"><i className="bi bi-box-arrow-right me-1" />Force logout</button></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'devices' && <IPUsageAnalytics loginLogs={advFiltered} lists={lists} />}
            {tab === 'control' && <DeviceControlPanel lists={lists} devices={devices} profiles={profiles} />}
            {tab === 'access' && <IPWhitelistManager lists={lists} knownIPs={knownIPs} />}
            {tab === 'settings' && <SecuritySettingsPanel lists={lists} />}
            {tab === 'security' && <SecurityEventsPanel />}

            <div className="text-center mt-3">
                <small className="text-muted"><i className="bi bi-circle-fill text-success me-1" style={{ fontSize: 8 }} />Live updates on</small>
            </div>
            {dialog}
        </div>
    );
};

export default LoginTrackingDashboard;
